import { useCallback } from 'react';
import { AddressForm, FormValidation } from '../../../global-types';
import { EnvelopeType } from '../../../global-types/envelope';
import { useIsOneToMany } from '../../../hooks/use-is-one-to-many';
import { useSkipAddressValidation } from '../../../hooks/use-skip-address-validation';
import { useAddressStore } from '../../../stores/address';
import { mapAddressesByType } from '../../../utils/address/map-address-by-type';
import { HandleAddressResubmit } from '../address-types';

type OnSkipValidation = (addressesToValidate: FormValidation[]) => void;

export type UseAddressSkipValidationProps = {
  envelope?: EnvelopeType | null;
  handleResubmit: HandleAddressResubmit;
};

export const useAddressSkipValidation = ({ envelope, handleResubmit }: UseAddressSkipValidationProps) => {
  // contexts
  const setAddressData = useAddressStore((store) => store.setAddressData);

  // hooks
  const skipAddressValidation: () => void = useSkipAddressValidation({ envelope });
  const isOneToMany = useIsOneToMany();

  // Function to be triggered when all the addresses where confirmed in address confirmation dialog
  const onSkipValidation = useCallback<OnSkipValidation>(
    (addressesToValidate) => {
      const skippedValidationAddresses = addressesToValidate
        .map((addressToValidate: FormValidation) => {
          const { contactId, originalAddressData } = addressToValidate;
          const addressID = contactId || '';
          const skippedAddress: Partial<AddressForm> = {
            ...originalAddressData,
            isQuickAddress: false,
            skip_usps_validation: true,
            address_id: String(addressID),
          };

          return skippedAddress ? { ...skippedAddress, skip_usps_validation: true } : null;
        })
        .filter((address) => address !== null) as AddressForm[];

      // Updates the context for other areas that need to use it
      const mappedAddresses = mapAddressesByType(skippedValidationAddresses);

      const senderAddress = mappedAddresses.sender;
      const recipientAddress = mappedAddresses.recipient;

      if (isOneToMany && senderAddress) {
        // if 1:M card, we need to assign the sender address
        // when IS_MULT_ADDRESS_ENABLED is disabled
        handleResubmit({ senderAddress });
        return;
      }

      // default is handling 1:1 card with both sender and recipient
      handleResubmit({ senderAddress, recipientAddress }).then(() => {
        setAddressData(mappedAddresses);
      });
    },
    [handleResubmit, isOneToMany, setAddressData],
  );

  return {
    onSkipValidation,
    skipAddressValidation,
  };
};
